import { useEffect } from 'react';

import { Row, Col } from 'antd';

import { Form, Fieldset, FullWidthSpace } from 'common/components';
import { ProductCategory } from './ProductCategory';

import { useCheckAllowEditPrivateLabel } from './hooks';
import { PACKAGE_LEVELS } from 'static/Constants';
import { useCheckIsProductOverlay } from 'hooks/product';

import { useCheckPermissionEditBrand } from 'hooks/useCheckPermissionEditBrand';

import { get, isEqual } from 'lodash';

import { ERROR_FIELD_NAME_COLLECTION } from '../preview-requirements/constants';
import { normalizeEmptyValue } from './utils';

import {
  ProductFieldset,
  SecurityFieldset,
} from 'common/components/product-add/components/ProductFieldset';
import { GtinFieldset } from 'common/components/product-add/components/GtinFieldset';
import { ClassificationFieldset } from 'common/components/product-add/components/ClassificationFieldset';
import { PackageWeightFieldset } from 'common/components/product-add/components/PackageWeightFieldset';
import { BrandFieldset } from 'common/components/product-add/components/BrandFieldset';
import { AdditionalImagerySection } from 'common/components/product-add/components/AdditionalImagerySection';
import { PrivateLabelFormItem } from 'common/components/product-add/components/PrivateLabel';
import { VariableWeightsAndMeasures } from 'common/components/product-add/components/VariableWeightsAndMeasures';
import { PLUItem } from 'common/components/product-add/components/PLUHanding';

import { useGetEnumsByEnumName, useUserInfo } from 'hooks';

import { RTA_FIELDS } from './components/constants';

import './SelfLoadedProductForm.less';

const formItemLayout = {
  labelCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 8,
    },
  },
  wrapperCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 15,
    },
  },
};

const SelfLoadedProductForm = (props) => {
  const {
    form,
    productData,
    formMode,
    targetMarketList,
    productEnums,
    currentHierarchy,
    brick,
    updateBrickCodeData,
    handleResetBrandId,
    setBrandId,
    editBrandVisible,
    setEditBrandVisible,
    brandThumb,
    setBrandThumb,
    errorInfo,
    memberIdBrand,
    setProductCategory,
    originProductFull,
    productFull,
    setProductImagery,
    productImagery,
    getFormPayload,
    validateErrors,
    setValidateErrors,
    statusValidateProps,
    validateProducts,
    onRevalidateProduct,
    // Hide active button to avoid duplicate the Active button. Because the button has been triggered when click active button.
    hideActiveButton = false,
    initialGtin,
  } = props;

  const isProductOverlay = useCheckIsProductOverlay({
    productFull: formMode === 'edit' ? productFull : null,
  });

  const { userInfo } = useUserInfo();

  const isOverlay = useCheckIsProductOverlay();

  const isEdit = formMode === 'edit';

  const hasPermissionEditBrand = useCheckPermissionEditBrand(
    memberIdBrand,
    userInfo
  );

  const handleNumberInput = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const productBasicUOM = useGetEnumsByEnumName('BasicUomEnum');

  const { checkAllowEditPrivateLabel } = useCheckAllowEditPrivateLabel({
    productData,
    isEdit,
    memberId: memberIdBrand,
  });

  //* permssion edit private label
  const showEditPrivateLabel = checkAllowEditPrivateLabel();

  useEffect(() => {
    if (!errorInfo) return;

    form.setFields(errorInfo);
  }, [errorInfo, form]);

  const upcGtinError = errorInfo?.find((errorItem) =>
    errorItem.name.includes(ERROR_FIELD_NAME_COLLECTION.AT_LEAST_GTIN_UPC)
  );

  const upcGtinErrorMessage = upcGtinError ? upcGtinError.errors[0] : '';
  const getOverlayProps = (path) => {
    const originValue = get(originProductFull, path);

    return {
      checkShowOverlayFn: ({ value }) => {
        const normalizedCurrent = normalizeEmptyValue(value);
        const normalizedOrigin = normalizeEmptyValue(originValue);

        return (
          !isEqual(normalizedCurrent, normalizedOrigin) &&
          isProductOverlay &&
          !!originProductFull
        );
      },
      originValue: originValue || 'N/A',
    };
  };

  const getBrickCodeOverlayProps = () => {
    const brickCodeFormatted = brick?.brickCode?.toString() ?? '';
    const originalBrickCode = originProductFull?.brickCode?.toString() ?? '';
    const isBrickCodeDifferent = brickCodeFormatted !== originalBrickCode;

    const originValue = originProductFull?.brickCode || 'N/A';
    return {
      showOverlay: isProductOverlay ? isBrickCodeDifferent : null,
      originValue: isProductOverlay ? originValue : null,
    };
  };

  const getBrickCodeNameOverlayProps = () => {
    const brickCodeNameFormatted = brick?.brickCodeName?.toString() ?? '';
    const originalBrickCodeName =
      originProductFull?.brickCodeName?.toString() ?? '';
    const isBrickCodeNameDifferent =
      brickCodeNameFormatted !== originalBrickCodeName;

    const originValue = originProductFull?.brickCodeName || 'N/A';
    return {
      showOverlay: isProductOverlay ? isBrickCodeNameDifferent : null,
      originValue: isProductOverlay ? originValue : null,
    };
  };

  const isVariableWeightsAndMeasures = Form.useWatch(
    'isVariableWeightsAndMeasures',
    form
  );

  const isFieldEditable = (fieldName) =>
    isOverlay && !RTA_FIELDS?.includes(fieldName);

  return (
    <Form
      form={form}
      initialValues={
        isEdit
          ? productData
          : {
              packageLevel: PACKAGE_LEVELS[0],
            }
      }
      {...formItemLayout}
      colon={false}
      name='Add-Product'
      preserve={false}
      className='add-product__modal'
    >
      <Row gutter={16} style={{ overflowX: 'hidden' }}>
        <Col span={8}>{showEditPrivateLabel && <PrivateLabelFormItem />}</Col>
        <Col
          span={16}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          <PLUItem isProductOverlay={isProductOverlay} />
          <VariableWeightsAndMeasures
            getOverlayProps={getOverlayProps}
            isVariableWeightsAndMeasures={isVariableWeightsAndMeasures}
            isProductOverlay={isProductOverlay}
            productData={productData}
          />
        </Col>
      </Row>
      <FullWidthSpace size={16}>
        <Row gutter={16}>
          <Col span={10}>
            <Fieldset title='Product' id='product-field'>
              <ProductFieldset
                getOverlayProps={getOverlayProps}
                productEnums={productEnums}
                targetMarketList={targetMarketList}
                isFieldEditable={isFieldEditable}
              />
              {isProductOverlay ? null : (
                <SecurityFieldset
                  getOverlayProps={getOverlayProps}
                  isProductOverlay={isProductOverlay}
                  productData={productData}
                  isEdit={isEdit}
                  formMode={formMode}
                  getFormPayload={getFormPayload}
                  validateErrors={validateErrors}
                  setValidateErrors={setValidateErrors}
                  hideActiveButton={hideActiveButton}
                  statusValidateProps={statusValidateProps}
                  validateProducts={validateProducts}
                  onRevalidateProduct={onRevalidateProduct}
                  productImagery={productImagery}
                />
              )}
            </Fieldset>
          </Col>
          <Col span={7}>
            <Fieldset title='GTIN' id='gtin-field'>
              <GtinFieldset
                productImagery={productImagery}
                getOverlayProps={getOverlayProps}
                upcGtinError={upcGtinError}
                upcGtinErrorMessage={upcGtinErrorMessage}
                handleNumberInput={handleNumberInput}
                formMode={formMode}
                isFieldEditable={isFieldEditable}
                memberId={isEdit ? productData?.ownerId : userInfo?.member?.id}
                getFormPayload={getFormPayload}
                prefixGtin={initialGtin}
              />
            </Fieldset>
          </Col>
          <Col span={7}>
            <Fieldset title='Packaging Weights and Measures' id='package-field'>
              <PackageWeightFieldset
                isFieldEditable={isFieldEditable}
                getOverlayProps={getOverlayProps}
                productBasicUOM={productBasicUOM}
                isVariableWeightsAndMeasures={isVariableWeightsAndMeasures}
                isProductOverlay={isProductOverlay}
              />
            </Fieldset>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            {hasPermissionEditBrand ? (
              <Fieldset title='Brand / Imagery'>
                <Row gutter={16}>
                  <Col span={8} xl={10} xxl={8}>
                    <BrandFieldset
                      handleResetBrandId={handleResetBrandId}
                      brandThumb={brandThumb}
                      setEditBrandVisible={setEditBrandVisible}
                      errorInfo={errorInfo}
                      editBrandVisible={editBrandVisible}
                      productData={productData}
                      isEdit={isEdit}
                      setBrandId={setBrandId}
                      setBrandThumb={setBrandThumb}
                      memberIdBrand={memberIdBrand}
                    />
                  </Col>
                  <Col span={12} xl={24} xxl={16}>
                    <AdditionalImagerySection
                      productImagery={productImagery}
                      setProductImagery={setProductImagery}
                      validateErrors={validateErrors}
                      memberId={
                        isEdit ? productData?.ownerId : userInfo?.member?.id
                      }
                      getFormPayload={getFormPayload}
                    />
                  </Col>
                </Row>
              </Fieldset>
            ) : null}
          </Col>
          <Col span={16}>
            <Fieldset
              title='Classification'
              id='classification-field'
              headingClassName={
                isProductOverlay
                  ? 'create-product-form__heading-fieldset-disabled'
                  : null
              }
            >
              <Row gutter={[36, 0]}>
                <Col span={14}>
                  <ClassificationFieldset
                    currentHierarchy={currentHierarchy}
                    getOverlayProps={getOverlayProps}
                    getBrickCodeOverlayProps={getBrickCodeOverlayProps}
                    updateBrickCodeData={updateBrickCodeData}
                    brick={brick}
                    getBrickCodeNameOverlayProps={getBrickCodeNameOverlayProps}
                    targetMarketList={targetMarketList}
                    onRevalidateProduct={onRevalidateProduct}
                  />
                </Col>
                <Col span={10}>
                  <ProductCategory
                    isProductOverlay={isOverlay}
                    setProductCategory={setProductCategory}
                    getOverlayProps={getOverlayProps}
                  />
                </Col>
              </Row>
            </Fieldset>
          </Col>
        </Row>
      </FullWidthSpace>
    </Form>
  );
};

export default SelfLoadedProductForm;
